import type { TableActionsItem } from 'components/Table/model/TableModel';
import { useFrontendLocalTable } from 'components/Table/model/useFrontendLocalTable';
import { useMemo, useState } from 'react';
import { addNewMenuItem } from '../shared/addNewMenuItem';
import { policyDeletionMessage } from '../shared/policyDeletionMessage';
import { useConfirmedDelete } from '../shared/useConfirmedDelete';
import { useWriteScopes } from '../shared/useWriteScopes';
import type { EndpointPolicyFormMode } from './EndpointPolicyForm';
import {
  type EndpointPolicy,
  useEndpointPoliciesApi,
  useEndpointPoliciesQuery,
} from './endpointPoliciesApi';
import {
  type EndpointPoliciesTableRow,
  createEndpointPoliciesTableColumns,
} from './endpointPoliciesTableColumns';

export type OpenedEndpointPolicy = {
  mode: EndpointPolicyFormMode;
  policy?: EndpointPolicy;
};

export const useEndpointPoliciesTable = () => {
  const [opened, setOpened] = useState<OpenedEndpointPolicy | null>(null);
  const scopes = useWriteScopes('/endpoint-policies', '/endpoint-policies/:id');
  const policiesQuery = useEndpointPoliciesQuery();
  const { deleteEndpointPolicy } = useEndpointPoliciesApi();

  const { confirmDeleteDialog, requestDelete } =
    useConfirmedDelete<EndpointPolicy>({
      header: 'Endpoint policy will be deleted',
      message: (item) =>
        policyDeletionMessage('endpoint', item.name, item.assignedRoles),
      deleteItem: (item) => deleteEndpointPolicy(item.id),
      successMessage: 'Endpoint policy deleted',
    });

  const columns = useMemo(
    () =>
      createEndpointPoliciesTableColumns({
        scopes,
        onOpen: (policy, mode) => setOpened({ policy, mode }),
        onDelete: (policy) => void requestDelete(policy),
      }),
    [requestDelete, scopes],
  );

  const rows = useMemo<EndpointPoliciesTableRow[]>(
    () =>
      (policiesQuery.data ?? []).map((policy) => ({
        id: policy.id,
        name: policy.name,
        description: policy.description,
        scopes: policy.apiScopeIds.length,
        roles: policy.assignedRoles.map((role) => role.name).join(', '),
        isSystem: policy.isSystem,
        policy,
      })),
    [policiesQuery.data],
  );

  const frontendSimpleFilter = useFrontendLocalTable({ columns, data: rows });

  const menuItems: TableActionsItem[] = [
    ...(scopes.canCreate
      ? [
          addNewMenuItem('endpoint-policies-add-new', () =>
            setOpened({ mode: 'add' }),
          ),
        ]
      : []),
  ];

  return {
    columns,
    confirmDeleteDialog,
    frontendSimpleFilter,
    menuItems,
    opened,
    policiesQuery,
    setOpened,
  };
};
